// Replaces oidc-client-ts automaticSilentRenew with Trimble Serial PKCE refresh (userManager.automaticSilentRenew is false).
import { log } from "@/config/logging";
import { reconnectApolloWebSocket } from "@/config/apollo";
import { userManager } from "@/provider/AuthProvider/userManager";
import {
  getTrimbleTokenRefreshDelayMs,
  isAuthCallbackPath,
  refreshTrimbleAccessToken,
} from "@/utils/silentTokenRefresh";
import type { User } from "oidc-client-ts";
import { useEffect, useRef, type RefObject } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation } from "react-router";

function isUserWithRefreshToken(
  user: User | null | undefined
): user is User & { refresh_token: string } {
  const token = user?.refresh_token;
  return token !== undefined && token.length > 0;
}

// Refresh before expiry so in-flight requests do not race an expired access token.
const REFRESH_BUFFER_SECONDS = 90;

async function refreshOidcSession(
  userRef: RefObject<User | null | undefined>
): Promise<void> {
  const currentUser = userRef.current;
  if (!isUserWithRefreshToken(currentUser)) {
    return;
  }

  try {
    const refreshedUser = await refreshTrimbleAccessToken(currentUser);
    await userManager.storeUser(refreshedUser);
    await userManager.events.load(refreshedUser);
    // HTTP reads in-memory OIDC user store per request; subscriptions need an explicit WS reconnect.
    reconnectApolloWebSocket();
  } catch (error: unknown) {
    log.error(error, { message: "Trimble token refresh failed" });
  }
}

export function useTokenRefresh() {
  const auth = useAuth();
  const { pathname } = useLocation();
  const userRef = useRef(auth.user);
  userRef.current = auth.user;

  const expiresAt = auth.user?.expires_at;
  const refreshToken = auth.user?.refresh_token;
  const isExpired = auth.user?.expired === true;

  useEffect(() => {
    if (isAuthCallbackPath(pathname)) {
      return;
    }

    if (
      expiresAt === undefined ||
      refreshToken === undefined ||
      refreshToken.length === 0
    ) {
      return;
    }

    if (isExpired) {
      void refreshOidcSession(userRef);
      return;
    }

    const refreshDelayMs = getTrimbleTokenRefreshDelayMs(
      expiresAt,
      REFRESH_BUFFER_SECONDS
    );

    const timeoutId = setTimeout(() => {
      void refreshOidcSession(userRef);
    }, refreshDelayMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [expiresAt, isExpired, pathname, refreshToken]);
}
